<template>
  <button
    :class="['custom-button', type, size, className, { loading: loading }]"
    :disabled="disabled || loading"
    @click="handleClick"
    type="button"
  >
    <AppIcon
      v-if="leftIcon"
      :name="leftIcon.name"
      :size="leftIcon.size ?? iconSize"
    />
    {{ label }}
    <AppIcon
      v-if="rightIcon"
      :name="rightIcon.name"
      :size="rightIcon.size ?? iconSize"
    />
  </button>
</template>

<script setup lang="ts">
import AppIcon from '@/components/common/app-icon/AppIcon.vue';
import { IconSize } from '@/models/icon-types';
import { OptionIcon } from '@/models/select-props-interfaces';
import { computed, ref } from 'vue';

export interface FormButtonProps {
  label: string;
  onClick: () => Promise<void> | void;
  type?: 'primary' | 'secondary' | 'destructive';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  leftIcon?: OptionIcon;
  rightIcon?: OptionIcon;
  disabled?: boolean;
}

const props = withDefaults(defineProps<FormButtonProps>(), {
  type: 'primary',
  size: 'medium',
  disabled: false
});

const loading = ref(false);

const handleClick = async () => {
  if (props.onClick) {
    try {
      loading.value = true;
      await props.onClick();
    } catch (error) {
      console.error('Error in button click handler:', error);
    } finally {
      loading.value = false;
    }
  }
};

const iconSize = computed<IconSize>(() => {
  switch (props.size) {
    case 'small':
      return 'body-xs';
    case 'medium':
      return 'body-s';
    case 'large':
      return 'body-m';
    default:
      return 'body-s';
  }
});
</script>

<style scoped lang="scss">
.custom-button {
  font-weight: $fw-medium;
  border-radius: $radius-2;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &.primary {
    color: $color-text-darker;
    background-color: $color-primary-500;
    border: 1px solid $color-primary-500;

    &:hover {
      transition: 0.2s ease;
      background-color: $color-primary-300;
      border: 1px solid $color-primary-300;
    }

    &:active {
      background-color: $color-primary-600;
      border: 1px solid $color-primary-600;
    }
  }

  &.secondary {
    border: 1px solid $color-neutral-200;
    color: $color-text-light;
    background-color: $color-neutral-0;

    &:hover {
      transition: 0.2s ease;
      background-color: $color-neutral-50;
    }

    &:active {
      background-color: $color-neutral-200;
    }
  }

  &.destructive {
    border: 1px solid $color-error-500;
    background-color: $color-neutral-0;
    color: $color-error-500;

    &:hover {
      transition: 0.2s ease;
      background-color: $color-error-500;
    }

    &:active {
      border: 1px solid $color-error-900;
      color: $color-error-900;
      background-color: $color-error-500;
    }
  }

  &.large {
    padding: $space-3 $space-5;
    font-size: $body-m-font-size;
    line-height: $body-m-line-height;
    gap: $space-3;
  }

  &.medium {
    padding: $space-2 $space-4;
    font-size: $body-s-font-size;
    line-height: $body-s-line-height;
    gap: $space-2;
  }

  &.small {
    padding: $space-1 $space-2;
    font-size: $body-xs-font-size;
    line-height: $body-xs-line-height;
    gap: $space-1;
  }

  &:disabled {
    background-color: $color-neutral-50;
    border: 1px solid $color-neutral-100;
    color: $color-text-light;
    cursor: not-allowed;

    &:hover,
    &:active,
    &:focus {
      background-color: $color-neutral-50;
      border: 1px solid $color-neutral-100;
      color: $color-text-light;
      box-shadow: none;
    }
  }

  &:focus {
    box-shadow: 0px 0px 0px 4px rgba(237, 236, 236, 0.8);
  }

  @keyframes loadingBackground {
    0% {
      background-position: -150% 0;
    }

    100% {
      background-position: 150% 0;
    }
  }

  &.loading {
    background: linear-gradient(
      to right,
      rgba($color-neutral-0, 0) 0%,
      rgba($color-neutral-100, 0.8) 25%,
      rgba($color-neutral-0, 0) 50%,
      rgba($color-neutral-100, 0.8) 75%,
      rgba($color-neutral-0, 0) 100%
    );
    background-size: 300% 100%;
    animation: loadingBackground 4s linear infinite;
  }
}
</style>
